/**
 * Two levels of "kind of work", assembled from one flat array.
 *
 * `jobs.categories` returns rows, not a tree, because the New job form wants
 * `optgroup`s and Settings wants indented editable rows. Both shapes are this
 * one function's output read differently, which is what stops a category
 * appearing in one screen's list and not the other's. Decision 71.
 */

export interface JobCategoryLike {
  id: string
  name: string
  parentId?: string | null
  sortOrder?: number
}

export interface JobCategorySection<T extends JobCategoryLike> {
  /** The group heading, or null for the categories that are not in one. */
  group: T | null
  /**
   * What is selectable under that heading.
   *
   * A group's own row is the first entry in its own section. A category that
   * becomes a parent may already be on thousands of jobs, so grouping work
   * under it must never quietly stop anyone choosing it.
   */
  options: T[]
}

/** Hand-ordered first, then alphabetical, so an unordered list still reads. */
function bySortThenName(a: JobCategoryLike, b: JobCategoryLike): number {
  const order = (a.sortOrder ?? 0) - (b.sortOrder ?? 0)
  return order !== 0 ? order : a.name.localeCompare(b.name)
}

/**
 * Groups categories for display.
 *
 * A row whose parent is not in the array — archived, or filtered out — is
 * treated as top-level rather than dropped. Losing an option is worse than
 * showing it without its heading.
 */
export function jobCategoryTree<T extends JobCategoryLike>(
  rows: readonly T[],
): JobCategorySection<T>[] {
  const present = new Set(rows.map((row) => row.id))

  const childrenOf = new Map<string, T[]>()
  const roots: T[] = []

  for (const row of rows) {
    const parentId = row.parentId ?? null

    if (parentId && present.has(parentId)) {
      const siblings = childrenOf.get(parentId)
      if (siblings) siblings.push(row)
      else childrenOf.set(parentId, [row])
    } else {
      roots.push(row)
    }
  }

  roots.sort(bySortThenName)
  for (const siblings of childrenOf.values()) siblings.sort(bySortThenName)

  const sections: JobCategorySection<T>[] = []
  let loose: T[] = []

  const flushLoose = () => {
    if (loose.length === 0) return
    sections.push({ group: null, options: loose })
    loose = []
  }

  for (const root of roots) {
    const children = childrenOf.get(root.id)

    if (!children) {
      loose.push(root)
      continue
    }

    // A group interrupts the loose run, so the ordering the office set is the
    // ordering they see rather than headings being swept to one end.
    flushLoose()
    sections.push({ group: root, options: [root, ...children] })
  }

  flushLoose()

  return sections
}
